import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { isStaleAssetError, reloadOnceForStaleAsset } from '@/lib/staleAssets'
import { isServiceUnavailable } from '@/lib/serviceAvailability'
import { rememberPostAuthRedirect, takePostAuthRedirect } from '@/lib/postAuthRedirect'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // Public routes
    {
      path: '/service-unavailable',
      name: 'service-unavailable',
      component: () => import('@/pages/auth/ServiceUnavailablePage.vue'),
      meta: { public: true }
    },
    {
      path: '/',
      name: 'home',
      component: () => import('@/pages/HomePage.vue'),
      meta: { public: true }
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/pages/auth/LoginPage.vue'),
      meta: { public: true, guestOnly: true }
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('@/pages/auth/RegisterPage.vue'),
      meta: { public: true, guestOnly: true }
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/pages/auth/ForgotPasswordPage.vue'),
      meta: { public: true, guestOnly: true }
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('@/pages/auth/ResetPasswordPage.vue'),
      meta: { public: true }
    },
    {
      path: '/account/delete/confirm',
      name: 'account-delete-confirm',
      component: () => import('@/pages/account/AccountDeleteConfirmPage.vue'),
      meta: { public: true }
    },
    {
      path: '/account-deleted',
      name: 'account-deleted',
      component: () => import('@/pages/account/AccountDeletedPage.vue'),
      meta: { public: true }
    },
    {
      path: '/auth/callback',
      name: 'auth-callback',
      component: () => import('@/pages/auth/OAuthCallbackPage.vue'),
      meta: { public: true }
    },
    {
      path: '/invite',
      name: 'invite-accept',
      component: () => import('@/pages/team/InviteAcceptPage.vue'),
      meta: { public: true }
    },
    {
      path: '/privacy',
      name: 'privacy',
      component: () => import('@/pages/legal/PrivacyPage.vue'),
      meta: { public: true }
    },
    {
      path: '/terms',
      name: 'terms',
      component: () => import('@/pages/legal/TermsPage.vue'),
      meta: { public: true }
    },

    // Protected routes
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('@/pages/dashboard/DashboardPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/tours',
      name: 'tours',
      component: () => import('@/pages/tours/ToursPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/tours/:uuid',
      name: 'tour-detail',
      component: () => import('@/pages/tours/TourDetailPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      // Statistics and Analytics are one page now.
      path: '/statistics',
      redirect: '/analytics'
    },
    {
      path: '/leads',
      name: 'leads',
      component: () => import('@/pages/leads/LeadsPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/analytics',
      name: 'analytics',
      component: () => import('@/pages/analytics/AnalyticsPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/analytics/:tourUuid',
      name: 'analytics-tour',
      component: () => import('@/pages/analytics/AnalyticsPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/settings',
      name: 'settings',
      component: () => import('@/pages/settings/SettingsPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      // Legacy subscription page (old builder links, bookmarks, emails) → the billing page.
      // `#credits` meant "buy credits": keep that intent as ?focus=credits.
      path: '/settings/subscription',
      redirect: to => ({
        path: '/billing',
        query: to.hash === '#credits' ? { focus: 'credits', ...to.query } : to.query,
      }),
    },
    {
      path: '/settings/:tab',
      name: 'settings-tab',
      component: () => import('@/pages/settings/SettingsPage.vue'),
      meta: { requiresAuth: true }
    },

    {
      path: '/billing',
      name: 'billing',
      component: () => import('@/pages/billing/BillingPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      // Videos are created and listed per tour; /videos is a stable link for emails and upsells.
      path: '/videos',
      redirect: to => ({ path: '/tours', query: to.query }),
    },
    {
      path: '/usage',
      name: 'usage',
      component: () => import('@/pages/usage/UsagePage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/team',
      name: 'team',
      component: () => import('@/pages/team/TeamPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/docs/api',
      name: 'docs-api',
      component: () => import('@/pages/docs/ApiDocsPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/docs/mcp',
      name: 'docs-mcp',
      component: () => import('@/pages/docs/McpDocsPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      // OAuth consent for AI assistants connecting to the MCP server (API worker /oauth/authorize sends users here)
      path: '/oauth/consent',
      name: 'oauth-consent',
      component: () => import('@/pages/oauth/OAuthConsentPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/support',
      name: 'support',
      component: () => import('@/pages/support/SupportPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/branding',
      name: 'branding',
      component: () => import('@/pages/branding/BrandingPage.vue'),
      meta: { requiresAdmin: true }
    },

    // Admin routes
    {
      path: '/admin',
      component: () => import('@/layouts/AdminLayout.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
      children: [
        {
          path: '',
          redirect: '/admin/dashboard'
        },
        {
          path: 'dashboard',
          name: 'admin-dashboard',
          component: () => import('@/pages/admin/AdminDashboard.vue')
        },
        {
          path: 'analytics',
          name: 'admin-analytics',
          component: () => import('@/pages/admin/AdminAnalytics.vue')
        },
        {
          path: 'users',
          name: 'admin-users',
          component: () => import('@/pages/admin/AdminUsers.vue')
        },
        {
          path: 'organizations',
          name: 'admin-organizations',
          component: () => import('@/pages/admin/AdminOrganizations.vue')
        },
        {
          path: 'tours',
          name: 'admin-tours',
          component: () => import('@/pages/admin/AdminTours.vue')
        },
        {
          path: 'subscriptions',
          name: 'admin-subscriptions',
          component: () => import('@/pages/admin/AdminSubscriptions.vue')
        },
        {
          path: 'support',
          name: 'admin-support',
          component: () => import('@/pages/admin/AdminSupport.vue')
        },
        {
          path: 'feedback',
          name: 'admin-feedback',
          component: () => import('@/pages/admin/FeedbackSurveys.vue')
        },
        {
          path: 'broadcasts',
          name: 'admin-broadcasts',
          component: () => import('@/pages/admin/AdminBroadcasts.vue')
        },
        {
          path: 'broadcasts/campaigns',
          name: 'admin-broadcast-campaigns',
          component: () => import('@/pages/admin/AdminBroadcasts.vue')
        },
        {
          path: 'broadcasts/campaigns/new',
          name: 'admin-broadcast-campaign-new',
          component: () => import('@/pages/admin/AdminBroadcasts.vue')
        },
        {
          path: 'broadcasts/campaigns/:campaignId/edit',
          name: 'admin-broadcast-campaign-edit',
          component: () => import('@/pages/admin/AdminBroadcasts.vue')
        },
        {
          path: 'broadcasts/campaigns/:campaignId/logs',
          name: 'admin-broadcast-campaign-logs',
          component: () => import('@/pages/admin/AdminBroadcasts.vue')
        },
        {
          path: 'broadcasts/templates',
          name: 'admin-broadcast-templates',
          component: () => import('@/pages/admin/AdminBroadcasts.vue')
        },
        {
          path: 'broadcasts/templates/new',
          name: 'admin-broadcast-template-new',
          component: () => import('@/pages/admin/AdminBroadcasts.vue')
        },
        {
          path: 'broadcasts/templates/:templateId/edit',
          name: 'admin-broadcast-template-edit',
          component: () => import('@/pages/admin/AdminBroadcasts.vue')
        },
        {
          path: 'broadcasts/workflows',
          name: 'admin-broadcast-workflows',
          component: () => import('@/pages/admin/AdminBroadcasts.vue')
        },
        {
          path: 'broadcasts/workflows/new',
          name: 'admin-broadcast-workflow-new',
          component: () => import('@/pages/admin/AdminBroadcasts.vue')
        },
        {
          path: 'broadcasts/workflows/:workflowId',
          name: 'admin-broadcast-workflow-detail',
          component: () => import('@/pages/admin/AdminBroadcasts.vue')
        },
        {
          path: 'broadcasts/provider',
          name: 'admin-broadcast-provider',
          component: () => import('@/pages/admin/AdminBroadcasts.vue')
        },
        {
          path: 'moderation',
          name: 'admin-moderation',
          component: () => import('@/pages/admin/AdminModeration.vue')
        },
        {
          // Sign-up / AI-agent legitimacy review queue (API: /api/admin/risk)
          path: 'risk',
          name: 'admin-risk',
          component: () => import('@/pages/admin/AdminRiskReview.vue')
        },
        {
          path: 'maintenance',
          name: 'admin-maintenance',
          component: () => import('@/pages/admin/AdminMaintenance.vue')
        },
        {
          path: 'organizations/:uuid',
          name: 'admin-org-detail',
          component: () => import('@/pages/admin/AdminOrgDetail.vue')
        },
        {
          path: 'users/:uuid',
          name: 'admin-user-detail',
          component: () => import('@/pages/admin/AdminUserDetail.vue')
        },
        {
          path: 'tours/:uuid',
          name: 'admin-tour-detail',
          component: () => import('@/pages/admin/AdminTourDetail.vue')
        },
        {
          path: 'email-logs',
          name: 'admin-email-logs',
          component: () => import('@/pages/admin/AdminEmailLogs.vue'),
          meta: { requiresAdmin: true }
        },
        {
          path: 'ai-usage',
          name: 'admin-ai-usage',
          component: () => import('@/pages/admin/AdminAiUsage.vue'),
          meta: { requiresAdmin: true }
        },
        {
          path: 'staff',
          name: 'admin-staff',
          component: () => import('@/pages/admin/AdminStaff.vue'),
          meta: { requiresAdmin: true }
        }
      ]
    },

    // About
    {
      path: '/about',
      name: 'about',
      component: () => import('@/pages/AboutPage.vue'),
      meta: { public: true }
    },

    // Catch all - 404
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/pages/NotFoundPage.vue'),
      meta: { public: true }
    }
  ]
})

// Navigation guards
router.beforeEach(async (to, _from, next) => {
  if (['service-unavailable', 'privacy', 'terms', 'account-delete-confirm', 'account-deleted'].includes(String(to.name))) {
    return next()
  }
  const needsAvailabilityCheck = to.meta.guestOnly || to.meta.requiresAuth ||
    to.matched.some(record => record.meta.requiresAuth) || ['invite-accept', 'auth-callback'].includes(String(to.name))
  if (needsAvailabilityCheck && await isServiceUnavailable()) return next('/service-unavailable')
  const authStore = useAuthStore()

  // Wait for auth to initialize
  if (!authStore.initialized) {
    await authStore.initialize()
  }

  const requiresAuth = to.meta.requiresAuth === true || to.matched.some(record => record.meta.requiresAuth)
  const requiresAdmin = to.meta.requiresAdmin === true || to.matched.some(record => record.meta.requiresAdmin)
  const guestOnly = to.meta.guestOnly === true

  // Redirect authenticated users away from guest-only pages (login, register)
  if (guestOnly && authStore.isAuthenticated) {
    return next(takePostAuthRedirect() || '/dashboard')
  }

  // Redirect unauthenticated users to login
  if (requiresAuth && !authStore.isAuthenticated) {
    // AI-assistant connections must survive sign-up + email verification, not just a login.
    if (to.path.startsWith('/oauth/')) rememberPostAuthRedirect(to.fullPath)
    return next({
      path: '/login',
      query: { redirect: to.fullPath }
    })
  }

  // Redirect non-admin users away from admin pages
  if (requiresAdmin && !authStore.isAdmin) {
    return next('/dashboard')
  }

  next()
})

// Handle stale chunk errors after a new deploy — force a full reload so the
// browser picks up fresh assets instead of showing a blank/error screen.
router.onError((err, to) => {
  const isChunkError = isStaleAssetError(err, to.fullPath) || err?.name === 'ChunkLoadError'

  if (isChunkError) {
    reloadOnceForStaleAsset(to.fullPath)
  }
})

export default router
